// Two-way mapping between the FilterPanel/Redux `filters` shape (plus
// sort) and the URL query string. Defaults are omitted on the way out
// so shared links stay readable, and missing params snap back to those
// same defaults on the way in. Used by useFilterUrlSync.
//
// Range axes use a context (decks / commanders / users) because each
// list surface declares a different subset in filterDefaults.js. Pass
// the same context on both ends so we don't write `decks_min=…` on the
// leaderboard or drop a slider you do show.

import { DEFAULT_RANGES, buildDefaultFilters, getRangeAxesForContext } from '../data/filterDefaults';
import { SORT_MAP } from '../data/redux/slices/leaderboardSlice';

// Short URL keys for each range axis — readable in the bar, no clashes.
const RANGE_KEY = { power: 'power', bracket: 'bracket', salt: 'salt', deckCount: 'decks' };

const SORT_BY_INDEX = Object.values(SORT_MAP).reduce((acc, v) => {
    if (v && typeof v === 'object' && v.index) acc[v.index] = v;
    return acc;
}, {});

const DEFAULT_SORT_INDEX = SORT_MAP.POWERLEVEL.index;
const DEFAULT_SORT_DIRECTION = SORT_MAP.DIRECTION.DESC;

const ANY = 'any';

const isAny = (v) => v == null || v === '' || v === ANY;

export function filtersToSearchParams(filters, sortBy, sortDirection, context = 'decks') {
    const params = new URLSearchParams();
    const f = filters || buildDefaultFilters(context);

    if (f.query) params.set('q', f.query);
    if (!isAny(f.archetypeMajor)) params.set('arch', f.archetypeMajor);
    if (!isAny(f.archetypeMinor)) params.set('sub', f.archetypeMinor);
    if (f.colorFilters) params.set('colors', f.colorFilters);

    const axes = getRangeAxesForContext(context);
    for (const axis of axes) {
        const r = f.ranges?.[axis];
        const def = DEFAULT_RANGES[axis];
        if (!r || !def) continue;
        const key = RANGE_KEY[axis] || axis;
        if (r.min != null && Number(r.min) !== Number(def.min)) params.set(`${key}_min`, String(r.min));
        if (r.max != null && Number(r.max) !== Number(def.max)) params.set(`${key}_max`, String(r.max));
    }

    const sortIndex = sortBy?.index;
    if (sortIndex && sortIndex !== DEFAULT_SORT_INDEX) params.set('sort', sortIndex);
    if (sortDirection && sortDirection !== DEFAULT_SORT_DIRECTION) params.set('dir', sortDirection);

    return params;
}

export function searchParamsToFilters(searchParams, context = 'decks') {
    const sp = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || '');
    const filters = buildDefaultFilters(context);

    if (sp.has('q')) filters.query = sp.get('q') || '';
    if (sp.has('arch')) filters.archetypeMajor = sp.get('arch') || '';
    if (sp.has('sub')) filters.archetypeMinor = sp.get('sub') || '';
    if (sp.has('colors')) filters.colorFilters = sp.get('colors') || '';

    const axes = getRangeAxesForContext(context);
    for (const axis of axes) {
        const key = RANGE_KEY[axis] || axis;
        const def = DEFAULT_RANGES[axis];
        if (!def) continue;
        const minRaw = sp.get(`${key}_min`);
        const maxRaw = sp.get(`${key}_max`);
        const min = minRaw == null || minRaw === '' ? def.min : Number(minRaw);
        const max = maxRaw == null || maxRaw === '' ? def.max : Number(maxRaw);
        filters.ranges[axis] = {
            min: Number.isFinite(min) ? min : def.min,
            max: Number.isFinite(max) ? max : def.max,
        };
    }

    const sortIndex = sp.get('sort');
    const sortBy = (sortIndex && SORT_BY_INDEX[sortIndex]) || SORT_MAP.POWERLEVEL;
    const dirRaw = sp.get('dir');
    const sortDirection = dirRaw === SORT_MAP.DIRECTION.ASC ? SORT_MAP.DIRECTION.ASC : DEFAULT_SORT_DIRECTION;

    return { filters, sortBy, sortDirection };
}

// True when every filter + sort value is at its default. Used to skip
// the initial URL-driven fetch when the URL contributes nothing — the
// app's hydrate path already issues the default query.
export function isDefaultFilterState(filters, sortBy, sortDirection, context = 'decks') {
    return filtersToSearchParams(filters, sortBy, sortDirection, context).toString() === '';
}
